import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, ReferenceArea, ResponsiveContainer,
} from 'recharts';
import api from '../services/api';
import type { LLMUsageReport, LLMUsageTimeseries, LLMSettingsOption, LLMUsageCallRow } from '../types';
import UsageUserFilter from '../components/UsageUserFilter';
import {
  brushToDateRange,
  bucketsSoFar,
  formatPeriodHeader,
  isCurrentOrFuturePeriod,
  shiftPeriod,
  type Period,
} from './usagePeriods';

type ChartMetric = 'calls' | 'tokens';

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm">
      <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">{label}</div>
      {payload.map((entry: { name: string; value: number; color: string }, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600 dark:text-gray-300">{entry.name}:</span>
          <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{formatCompact(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

// Determine which rate limits are relevant for the chart metric + period
function getRelevantLimits(
  ts: LLMUsageTimeseries,
  metric: ChartMetric,
  period: Period,
): { value: number; label: string; color: string }[] {
  const lines: { value: number; label: string; color: string }[] = [];
  if (metric === 'calls') {
    if (period === 'hour' && ts.rate_limit_rpm) {
      lines.push({ value: ts.rate_limit_rpm, label: `RPM limit (${formatCompact(ts.rate_limit_rpm)}/min)`, color: '#f97316' });
    }
    if (period === 'day' && ts.rate_limit_rpd) {
      lines.push({ value: Math.ceil(ts.rate_limit_rpd / 24), label: `RPD limit (${formatCompact(ts.rate_limit_rpd)}/day, ~${Math.ceil(ts.rate_limit_rpd / 24)}/hr)`, color: '#ef4444' });
    }
    if (period === 'month' && ts.rate_limit_rpd) {
      lines.push({ value: ts.rate_limit_rpd, label: `RPD limit (${formatCompact(ts.rate_limit_rpd)}/day)`, color: '#ef4444' });
    }
  } else {
    // tokens
    if (period === 'hour' && ts.rate_limit_tpm) {
      lines.push({ value: ts.rate_limit_tpm, label: `TPM limit (${formatCompact(ts.rate_limit_tpm)}/min)`, color: '#f97316' });
    }
    if (period === 'day' && ts.rate_limit_tpd) {
      lines.push({ value: Math.ceil(ts.rate_limit_tpd / 24), label: `TPD limit (${formatCompact(ts.rate_limit_tpd)}/day, ~${formatCompact(Math.ceil(ts.rate_limit_tpd / 24))}/hr)`, color: '#ef4444' });
    }
    if (period === 'month' && ts.rate_limit_tpd) {
      lines.push({ value: ts.rate_limit_tpd, label: `TPD limit (${formatCompact(ts.rate_limit_tpd)}/day)`, color: '#ef4444' });
    }
  }
  return lines;
}

export default function TokenUsageReport() {
  const [period, setPeriod] = useState<Period>('month');
  const [periodDate, setPeriodDate] = useState<string | undefined>(undefined);
  const [report, setReport] = useState<LLMUsageReport | null>(null);
  const [timeseries, setTimeseries] = useState<LLMUsageTimeseries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLlmId, setSelectedLlmId] = useState<number | undefined>(undefined);
  const [llmOptions, setLlmOptions] = useState<LLMSettingsOption[]>([]);
  const [chartMetric, setChartMetric] = useState<ChartMetric>('calls');
  // '' = my own usage. Admin-only; see UsageUserFilter.
  const [userScope, setUserScope] = useState<string>('');
  const [brushRange, setBrushRange] = useState<[number, number] | null>(null);
  // Drag-to-select on chart
  const [dragStart, setDragStart] = useState<string | null>(null);
  const [dragEnd, setDragEnd] = useState<string | null>(null);
  const [expandedLlmId, setExpandedLlmId] = useState<number | null>(null);
  const [callDetails, setCallDetails] = useState<LLMUsageCallRow[] | null>(null);
  const [callDetailsLoading, setCallDetailsLoading] = useState(false);
  const [callDetailsTotal, setCallDetailsTotal] = useState(0);

  useEffect(() => {
    api.getLLMSettingsOptions().then(setLlmOptions).catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [reportData, tsData] = await Promise.all([
        api.getLLMUsageReport(period, selectedLlmId, periodDate, userScope || undefined),
        api.getLLMUsageTimeseries(period, selectedLlmId, periodDate, userScope || undefined),
      ]);
      setReport(reportData);
      setTimeseries(tsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage report');
    } finally {
      setLoading(false);
    }
  }, [period, selectedLlmId, periodDate, userScope]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handlePeriodChange = (p: Period) => {
    setPeriod(p);
    setPeriodDate(undefined);
    setBrushRange(null);
    setExpandedLlmId(null);
    setCallDetails(null);
  };

  const handleLlmChange = (id: number | undefined) => {
    setSelectedLlmId(id);
    setPeriodDate(undefined);
    setBrushRange(null);
    setExpandedLlmId(null);
    setCallDetails(null);
  };

  const handlePrev = () => {
    if (!report?.period_start) return;
    setPeriodDate(shiftPeriod(report.period_start, period, -1));
    setBrushRange(null);
    setExpandedLlmId(null);
    setCallDetails(null);
  };

  const handleNext = () => {
    if (!report?.period_start) return;
    setPeriodDate(shiftPeriod(report.period_start, period, 1));
    setBrushRange(null);
    setExpandedLlmId(null);
    setCallDetails(null);
  };

  const nextDisabled = report?.period_start
    ? isCurrentOrFuturePeriod(report.period_start, period)
    : true;

  const formatTokens = (n: number) => formatCompact(n);

  const formatCost = (cost: number | null) => cost == null ? '-' : `$${cost.toFixed(4)}`;

  const handleRowClick = async (llmId: number) => {
    if (expandedLlmId === llmId) {
      setExpandedLlmId(null);
      setCallDetails(null);
      return;
    }
    if (!report?.period_start || !timeseries) return;
    setExpandedLlmId(llmId);
    setCallDetails(null);
    setCallDetailsLoading(true);
    try {
      const { start, end } = brushToDateRange(
        brushRange,
        period,
        report.period_start,
        timeseries.buckets.length,
      );
      const resp = await api.getLLMUsageCalls(start, end, llmId, undefined, userScope || undefined);
      setCallDetails(resp.calls);
      setCallDetailsTotal(resp.total_count);
    } catch {
      setCallDetails([]);
    } finally {
      setCallDetailsLoading(false);
    }
  };

  const finalizeDrag = () => {
    if (!dragStart || !dragEnd || !timeseries) {
      setDragStart(null);
      setDragEnd(null);
      return;
    }
    // Resolve labels against the full bucket list for absolute indices
    const allLabels = timeseries.buckets.map((b) => b.label);
    let startIdx = allLabels.indexOf(dragStart);
    let endIdx = allLabels.indexOf(dragEnd);
    if (startIdx === -1 || endIdx === -1) {
      setDragStart(null);
      setDragEnd(null);
      return;
    }
    // Ensure start <= end
    if (startIdx > endIdx) [startIdx, endIdx] = [endIdx, startIdx];
    // If the user selected everything, clear
    if (startIdx === 0 && endIdx === allLabels.length - 1) {
      setBrushRange(null);
    } else {
      setBrushRange([startIdx, endIdx]);
    }
    setExpandedLlmId(null);
    setCallDetails(null);
    setDragStart(null);
    setDragEnd(null);
  };

  const clearSelection = () => {
    setBrushRange(null);
    setExpandedLlmId(null);
    setCallDetails(null);
  };

  // Build chart data — trim future buckets past now+1h, then zoom into selection
  const hasTimeseries = timeseries && timeseries.buckets.length > 0;
  const allBuckets = React.useMemo(
    () => bucketsSoFar(timeseries?.buckets ?? [], period, report?.period_start),
    [timeseries, report?.period_start, period],
  );
  const chartData = brushRange
    ? allBuckets.slice(brushRange[0], brushRange[1] + 1)
    : allBuckets;

  const shiftRange = (direction: -1 | 1) => {
    const maxIdx = allBuckets.length - 1;
    if (maxIdx < 1) return;
    // If not zoomed, create a window (~quarter of the data) and shift into it
    const curStart = brushRange ? brushRange[0] : 0;
    const curEnd = brushRange ? brushRange[1] : maxIdx;
    const windowSize = curEnd - curStart + 1;
    const step = brushRange ? Math.max(1, Math.floor(windowSize / 2)) : Math.max(1, Math.floor(allBuckets.length / 4));
    let newStart: number, newEnd: number;
    if (!brushRange) {
      // First scroll: create a window
      if (direction === -1) {
        newEnd = maxIdx;
        newStart = Math.max(0, maxIdx - step + 1);
      } else {
        newStart = 0;
        newEnd = Math.min(maxIdx, step - 1);
      }
    } else {
      newStart = curStart + direction * step;
      newEnd = curEnd + direction * step;
      if (newStart < 0) { newEnd -= newStart; newStart = 0; }
      if (newEnd > maxIdx) { newStart -= (newEnd - maxIdx); newEnd = maxIdx; }
      newStart = Math.max(0, newStart);
      newEnd = Math.min(maxIdx, newEnd);
    }
    if (newStart === 0 && newEnd === maxIdx) {
      clearSelection();
    } else {
      setBrushRange([newStart, newEnd]);
      setExpandedLlmId(null);
      setCallDetails(null);
    }
  };

  const limits = hasTimeseries ? getRelevantLimits(timeseries, chartMetric, period) : [];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Token Usage</h2>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {/* Granularity pills */}
        <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
          {(['hour', 'day', 'month', 'year'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => handlePeriodChange(p)}
              className={`px-3 py-1.5 capitalize ${
                period === p ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Period navigation */}
        {report?.period_start && (
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrev}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              title="Previous period"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[140px] text-center">
              {formatPeriodHeader(report.period_start, period)}
            </span>
            <button
              onClick={handleNext}
              disabled={nextDisabled}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next period"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Model filter */}
        <select
          value={selectedLlmId ?? ''}
          onChange={(e) => handleLlmChange(e.target.value ? Number(e.target.value) : undefined)}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All models</option>
          {llmOptions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.display_name}{m.is_system ? ' [System]' : ''}
            </option>
          ))}
        </select>

        <UsageUserFilter
          value={userScope}
          onChange={(next) => {
            setUserScope(next);
            // The period we are on came from the OLD scope's most recent data;
            // reset so the panel re-anchors on the new one instead of showing an
            // empty month for someone who was not working then.
            setPeriodDate(undefined);
            clearSelection();
          }}
        />
      </div>

      {/* Summary bar */}
      {report && report.rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Input Tokens</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatTokens(report.total_input_tokens)}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Output Tokens</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatTokens(report.total_output_tokens)}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Total Tokens</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatTokens(report.total_tokens)}</div>
          </div>
          <div
            className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3"
            title="Input tokens served from the Anthropic prompt cache (billed ~0.1x). Included in Input Tokens."
          >
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Cached Reads</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatTokens(report.total_cached_input_tokens)}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Total Cost</div>
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{formatCost(report.total_cost_usd)}</div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {/* Chart */}
      {!loading && hasTimeseries && (
        <div className="bg-white dark:bg-gray-800 border rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {chartMetric === 'calls' ? 'API Calls' : 'Tokens'}{' '}
              {period === 'hour' ? 'per Minute' : period === 'day' ? 'per Hour' : period === 'month' ? 'per Day' : 'per Month'}
            </h3>
            <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden text-xs">
              <button
                onClick={() => setChartMetric('calls')}
                className={`px-2.5 py-1 ${chartMetric === 'calls' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                Calls
              </button>
              <button
                onClick={() => setChartMetric('tokens')}
                className={`px-2.5 py-1 ${chartMetric === 'tokens' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                Tokens
              </button>
            </div>
          </div>

          <div className="w-full" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                onMouseDown={(e) => {
                  if (e?.activeLabel != null) setDragStart(String(e.activeLabel));
                }}
                onMouseMove={(e) => {
                  if (dragStart && e?.activeLabel != null) setDragEnd(String(e.activeLabel));
                }}
                onMouseUp={finalizeDrag}
                onMouseLeave={() => {
                  if (dragStart) {
                    setDragStart(null);
                    setDragEnd(null);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  interval={chartData.length <= 12 ? 0 : chartData.length <= 30 ? 1 : Math.floor(chartData.length / 15)}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatCompact}
                  width={48}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<ChartTooltip />} />
                {chartMetric === 'calls' ? (
                  <Line type="monotone" dataKey="call_count" name="Calls" stroke="#6366f1" strokeWidth={2} dot={chartData.length <= 30} />
                ) : (
                  <>
                    <Line type="monotone" dataKey="input_tokens" name="Input" stroke="#6366f1" strokeWidth={2} dot={chartData.length <= 30} />
                    <Line type="monotone" dataKey="output_tokens" name="Output" stroke="#a78bfa" strokeWidth={2} dot={chartData.length <= 30} />
                  </>
                )}
                {limits.map((lim, i) => (
                  <ReferenceLine
                    key={i}
                    y={lim.value}
                    stroke={lim.color}
                    strokeDasharray="6 3"
                    strokeWidth={1.5}
                    label={{
                      value: lim.label,
                      position: 'insideTopRight',
                      fontSize: 11,
                      fill: lim.color,
                    }}
                  />
                ))}
                {/* Drag highlight while selecting */}
                {dragStart && dragEnd && (
                  <ReferenceArea x1={dragStart} x2={dragEnd} strokeOpacity={0.3} fill="#6366f1" fillOpacity={0.15} />
                )}
                <Legend
                  verticalAlign="bottom"
                  height={28}
                  iconType="line"
                  iconSize={14}
                  wrapperStyle={{ fontSize: 12 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Rate limit legend (when limits are shown) */}
          {limits.length > 0 && (
            <div className="flex flex-wrap gap-4 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              {limits.map((lim, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <span className="w-4 border-t-2 border-dashed" style={{ borderColor: lim.color }} />
                  {lim.label}
                </div>
              ))}
            </div>
          )}

          {/* Time range controls */}
          {hasTimeseries && allBuckets.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium mr-1">Range:</span>
              <button
                onClick={() => shiftRange(-1)}
                disabled={brushRange != null && brushRange[0] === 0}
                className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Shift range earlier"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <select
                value={brushRange ? brushRange[0] : 0}
                onChange={(e) => {
                  const newStart = Number(e.target.value);
                  const curEnd = brushRange ? brushRange[1] : allBuckets.length - 1;
                  const end = Math.max(newStart, curEnd);
                  if (newStart === 0 && end === allBuckets.length - 1) {
                    clearSelection();
                  } else {
                    setBrushRange([newStart, end]);
                    setExpandedLlmId(null);
                    setCallDetails(null);
                  }
                }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {allBuckets.map((b, i) => (
                  <option key={i} value={i}>{b.label}</option>
                ))}
              </select>
              <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
              <select
                value={brushRange ? brushRange[1] : allBuckets.length - 1}
                onChange={(e) => {
                  const newEnd = Number(e.target.value);
                  const curStart = brushRange ? brushRange[0] : 0;
                  const start = Math.min(curStart, newEnd);
                  if (start === 0 && newEnd === allBuckets.length - 1) {
                    clearSelection();
                  } else {
                    setBrushRange([start, newEnd]);
                    setExpandedLlmId(null);
                    setCallDetails(null);
                  }
                }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {allBuckets.map((b, i) => (
                  <option key={i} value={i}>{b.label}</option>
                ))}
              </select>
              <button
                onClick={() => shiftRange(1)}
                disabled={brushRange != null && brushRange[1] >= allBuckets.length - 1}
                className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Shift range later"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {brushRange && (
                <button
                  onClick={clearSelection}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium ml-2 px-2 py-1 rounded border border-indigo-200 dark:border-indigo-800 hover:border-indigo-300 dark:hover:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30"
                >
                  Reset zoom
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
      ) : !report || report.rows.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
          No usage recorded yet
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Model</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Calls</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Input</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Output</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" title="Prompt-cache reads (billed ~0.1x), included in Input">Cached</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {report.rows.map((row, idx) => {
                const isExpanded = expandedLlmId === row.llm_id;
                return (
                  <React.Fragment key={idx}>
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer select-none"
                      onClick={() => handleRowClick(row.llm_id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <svg
                            className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <div>
                            <div className="text-gray-900 dark:text-gray-100">{row.llm_name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{row.provider ? `${row.provider} / ` : ''}{row.model_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300 font-mono text-sm">{row.call_count}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300 font-mono text-sm">{formatTokens(row.input_tokens)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300 font-mono text-sm">{formatTokens(row.output_tokens)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300 font-mono text-sm">{formatTokens(row.total_tokens)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300 font-mono text-sm">{row.cached_input_tokens ? formatTokens(row.cached_input_tokens) : '-'}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300 font-mono text-sm">{formatCost(row.cost_usd)}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="bg-gray-50 dark:bg-gray-800 px-4 py-3">
                          {callDetailsLoading ? (
                            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">Loading call details...</div>
                          ) : !callDetails || callDetails.length === 0 ? (
                            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">No individual calls found in this range</div>
                          ) : (
                            <>
                              {callDetailsTotal > callDetails.length && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                  Showing {callDetails.length} of {callDetailsTotal} calls
                                </div>
                              )}
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                                    <th className="text-left py-1.5 pr-3 font-medium">Time</th>
                                    <th className="text-left py-1.5 pr-3 font-medium">Stage</th>
                                    <th className="text-right py-1.5 pr-3 font-medium">Input</th>
                                    <th className="text-right py-1.5 pr-3 font-medium">Output</th>
                                    <th className="text-right py-1.5 pr-3 font-medium">Total</th>
                                    <th className="text-right py-1.5 pr-3 font-medium" title="Prompt-cache reads (billed ~0.1x)">Cached</th>
                                    <th className="text-right py-1.5 pr-3 font-medium">Cost</th>
                                    <th className="text-right py-1.5 font-medium">Duration</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                  {callDetails.map((call) => (
                                    <tr key={call.id} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                                      <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300 font-mono whitespace-nowrap">
                                        {new Date(call.created_at).toLocaleTimeString('en-US', { hour12: false })}
                                      </td>
                                      <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-300">{call.stage}</td>
                                      <td className="py-1.5 pr-3 text-right text-gray-600 dark:text-gray-300 font-mono">{formatTokens(call.input_tokens)}</td>
                                      <td className="py-1.5 pr-3 text-right text-gray-600 dark:text-gray-300 font-mono">{formatTokens(call.output_tokens)}</td>
                                      <td className="py-1.5 pr-3 text-right text-gray-600 dark:text-gray-300 font-mono">{formatTokens(call.total_tokens)}</td>
                                      <td className="py-1.5 pr-3 text-right text-gray-600 dark:text-gray-300 font-mono">{call.cached_input_tokens ? formatTokens(call.cached_input_tokens) : '-'}</td>
                                      <td className="py-1.5 pr-3 text-right text-gray-600 dark:text-gray-300 font-mono">{formatCost(call.cost_usd)}</td>
                                      <td className="py-1.5 text-right text-gray-600 dark:text-gray-300 font-mono">
                                        {call.duration_seconds != null ? `${call.duration_seconds}s` : '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
